import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DetailEventComponent } from '../detail-event/detail-event.component';
import { PsaService } from '../../../../services/psa.service';
import { SnackbarService } from '../../../../services/snackbar.service';

/**
 * @Author Tommaso Lazzaretti
 *
 * AttachEventComponent
 *
 * This Angular component is responsible for attaching events to a specific action.
 * It provides functionality for searching, filtering, and selecting events from a list
 * as well as creating new events. The component also allows managing ranks (probabilities)
 * for the associated events.
 *
 * Dependencies:
 * - MAT_DIALOG_DATA: Data passed to the component when it is opened as a dialog.
 * - MatDialog: Service to handle opening and managing dialogs.
 * - MatDialogRef: Reference to the dialog, allowing it to be closed programmatically.
 * - PsaService: Service that contains API calls for retrieving and managing events.
 */
@Component({
  selector: 'ebw-monorepo-attach-event',
  templateUrl: './attach-event.component.html',
  styleUrls: ['./attach-event.component.scss']
})
export class AttachEventComponent {
  @ViewChild('myInput') myInput: ElementRef<HTMLInputElement>;

  searchTerm: string = '';
  associatedEvents: any[] = [];
  listEvents: any[] = [];
  listEventsFiltered: any[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _dialog: MatDialog,
    private _dialogRef: MatDialogRef<AttachEventComponent>,
    private _psaService: PsaService,
    private _snackbarService: SnackbarService
  ) {
    this._psaService._getListEvents({ tipologia: this.data?.tipologia }).subscribe((actions) => {
      this.listEvents = actions;

      if (this.data && this.data.associatedEvents) {
        // recupero la lista di azioni già associate e le seleziono di default
        this.associatedEvents = this.data.associatedEvents.map((e) => {
          e.old = true;
          e = { ...e, ...e.evento };
          return e;
        });
      }

      this._filterOptions();
    });
  }

  /**
   * Filters the list of events based on the search term and excludes events
   * already associated with the current action.
   */
  _filterOptions() {
    const filterValue = this.searchTerm ? this.searchTerm.toLowerCase() : null;
    this.listEventsFiltered = this.listEvents.filter((e) => !this.associatedEvents.some((ae) => ae.id === e.id));

    if (filterValue)
      this.listEventsFiltered = this.listEventsFiltered.filter((option) =>
        option.nome.toLowerCase().includes(filterValue)
      ); // Filtra in base al valore dell'input
  }

  onInput(): void {
    this._filterOptions();
  }

  onRemoveEvent(event) {
    const temp = this.associatedEvents.filter((a) => a.id !== event.id);
    this.associatedEvents = [...temp];
    this._filterOptions();
  }

  /**
   * Handles the selection of an event from the list, adding it to the associated events list
   * and clearing the search input field.
   */
  onOptionSelected(selectedValue): void {
    const isAlreadyPresent = this.associatedEvents.some((entity) => entity.id === selectedValue.id);

    // Aggiungi l'entità selezionata alla lista nel caso già presente mando messaggio snack
    if (isAlreadyPresent) {
      this._snackbarService.open('Elemento già inserito');
    } else {
      // Aggiungi l'elemento selezionato alla lista
    }

    this.associatedEvents.push(selectedValue);
    // Svuota il contenuto dell'input
    this.searchTerm = null;
    this.myInput.nativeElement.value = null;

    this._filterOptions();
  }

  /**
   * Checks if any event is currently selected from the event list.
   *
   * @returns {boolean} True if at least one event is selected, otherwise false.
   */
  isAnyEventSelected(): boolean {
    return this.associatedEvents.some((event) => !event['old']);
  }

  /**
   * Opens a dialog for creating a new event using the DetailEventComponent.
   * After the event is created, it adds the event to the list of associated events.
   */
  createNewEvent() {
    const ref = this._dialog.open(DetailEventComponent, {
      maxWidth: '100vw',
      maxHeight: '100%',
      autoFocus: false
    });

    ref.afterClosed().subscribe((resp) => {
      if (!resp) {
        return;
      }
      // aggiungo event alla lista esistente ma deselezionato
      this.associatedEvents.push({ ...resp, old: false });
      this._filterOptions();
    });
  }

  /**
   * Handles changes to the rank (probability) of an associated event.
   *
   * @param {any} evento - The event whose rank is being modified.
   * @param {number} rank - The new rank value.
   */
  onRankChange(evento, rank) {
    // Logica per salvare o gestire il cambiamento del rank
    evento.probabilita = rank;
  }

  onClose() {
    this._dialogRef.close();
  }

  /**
   * Saves the associated events by mapping the response to the format expected by the backend
   * and closes the dialog, returning the selected events.
   */
  onSave() {
    this._dialogRef.close(this.mapRespForBE(this.associatedEvents));
  }

  /**
   * Maps the response data to the format required by the backend, filtering only the selected events
   * and including their ID and rank (probability).
   *
   * @param {any[]} input - List of associated events.
   * @returns {any[]} Mapped list of events in the backend format.
   */
  mapRespForBE(input) {
    return input
      .filter((item) => !item.old) // Filtra solo gli elementi con selected: true
      .map((item) => ({
        evento_rischio_id: item.id,
        // probabilita' non scelta = non valutata, resta vuota (prima diventava 1 ed entrava nel calcolo del rischio)
        probabilita: item.probabilita || null
      }));
  }
}
